import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "../components/Header";
import Toast from "../components/Toast";
import { stockService } from "../services/apiService";

const ProductCategory = () => {
  const [form, setForm]             = useState({ name: "", code: "", discount: "", gstRate: "", hsnCode: "" });
  const [errors, setErrors]         = useState({});
  const [editingCode, setEditingCode] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [toast, setToast]           = useState(null);

  const showToast = (type, message, identifier = null) =>
    setToast({ type, message, identifier });

  const getErrMsg = (err, fallback) =>
    err?.response?.data?.errorMessage ||
    err?.response?.data?.message ||
    (typeof err?.response?.data === "string" ? err.response.data : null) ||
    fallback;

  const location = useLocation();

  const loadCategories = async () => {
    setLoading(true);
    setCategories([]);
    try {
      const data = await stockService.getAllCategories();
      const list = Array.isArray(data) ? data : [];
      setCategories(list);
    } catch {
      setCategories([]);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { loadCategories(); }, [location.key]);

  const filtered = categories.filter((c) => {
    const q = searchTerm.toLowerCase();
    return (c.type || c.name || c.categoryName || "").toLowerCase().includes(q) ||
           (c.code || "").toLowerCase().includes(q);
  });

  const getTypeName = (c) => c.type || c.name || c.categoryName || "";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: name === "code" ? value.toUpperCase() : value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name?.trim()) e.name = "Type is required";
    if (!form.code?.trim()) e.code = "Code is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        type: form.name.trim(),
        code: form.code.trim(),
        discount: form.discount !== "" ? parseFloat(form.discount) : null,
        gstRate: form.gstRate !== "" ? parseFloat(form.gstRate) : null,
        hsnCode: form.hsnCode.trim() || null,
      };
      if (editingCode) {
        await stockService.updateCategory({ ...payload, code: editingCode });
        const updated = { type: form.name.trim(), code: editingCode, discount: payload.discount, gstRate: payload.gstRate, hsnCode: payload.hsnCode };
        setCategories((prev) => prev.map((c) => c.code === editingCode ? { ...c, ...updated } : c));
        showToast("success", "Category updated successfully", form.name);
      } else {
        const saved = await stockService.createCategory(payload);
        const newCat = saved || { type: form.name.trim(), code: form.code.trim(), discount: payload.discount, gstRate: payload.gstRate, hsnCode: payload.hsnCode };
        setCategories((prev) => [...prev, newCat]);
        showToast("success", "Category created successfully", form.name);
      }
      setForm({ name: "", code: "", discount: "", gstRate: "", hsnCode: "" });
      setErrors({});
      setEditingCode(null);
    } catch (err) {
      showToast("error", getErrMsg(err, "Failed to save category"));
    } finally { setSaving(false); }
  };

  const handleEdit = (cat) => {
    setForm({
      name: cat.type || cat.name || cat.categoryName || "",
      code: cat.code,
      discount: cat.discount ?? cat.defaultDiscount ?? "",
      gstRate: cat.gstRate ?? "",
      hsnCode: cat.hsnCode ?? "",
    });
    setErrors({});
    setEditingCode(cat.code);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReset = () => {
    setForm({ name: "", code: "", discount: "", gstRate: "", hsnCode: "" });
    setErrors({});
    setEditingCode(null);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />
      {toast && <Toast type={toast.type} identifier={toast.identifier} message={toast.message} onClose={() => setToast(null)} />}

      {/* ── Form Section ── */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex-shrink-0">
        <h2 className="text-left text-sm font-bold text-blue-800 px-4 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex items-center justify-between">
          <span>{editingCode ? "✏️ Update Product Category" : "➕ Create Product Category"}</span>
          {editingCode && (
            <button onClick={handleReset}
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition flex items-center gap-1">
              <i className="fas fa-plus-circle" /> Create Product Category
            </button>
          )}
        </h2>
        <div className="p-2">
          <div className="flex flex-wrap gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Type <span className="text-red-500">*</span>
              </label>
              <input
                type="text" name="name" value={form.name} onChange={handleChange}
                placeholder="e.g. Medicine"
                className={`w-44 px-3 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${
                  errors.name ? "border-red-400 bg-red-50" : "border-blue-300"
                }`}
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text" name="code" value={form.code} onChange={handleChange}
                placeholder="e.g. MED"
                disabled={!!editingCode}
                className={`w-44 px-3 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${
                  errors.code ? "border-red-400 bg-red-50" : "border-blue-300"
                } ${editingCode ? "bg-gray-100 cursor-not-allowed" : ""}`}
              />
              {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Discount %</label>
              <input
                type="number" name="discount" value={form.discount} onChange={handleChange}
                placeholder="e.g. 10" min="0" max="100" step="0.01"
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">GST Rate %</label>
              <input
                type="number" name="gstRate" value={form.gstRate} onChange={handleChange}
                placeholder="e.g. 12" min="0" max="28" step="0.01"
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">HSN Code</label>
              <input
                type="text" name="hsnCode" value={form.hsnCode} onChange={handleChange}
                placeholder="e.g. 30049011" maxLength={8}
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={handleReset}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs transition">
              <i className="fas fa-redo" /> Reset
            </button>
            <button onClick={handleSave} disabled={saving}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${
                saving ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"
              }`}>
              {saving
                ? <><i className="fas fa-spinner fa-spin" /> Saving...</>
                : editingCode
                  ? <><i className="fas fa-check" /> Update Category</>
                  : <><i className="fas fa-save" /> Save Category</>}
            </button>
          </div>
        </div>
      </section>

      {/* ── Search + Results Section ── */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-2 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          <h2 className="text-base font-bold text-blue-800">
            🔍 Product Categories
            {categories.length > 0 && (
              <span className="text-xs font-normal text-blue-600 ml-2">({categories.length})</span>
            )}
          </h2>
          <input
            type="text" value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by type or code..."
            className="w-52 px-3 py-1.5 border border-blue-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
          />
        </div>
        <div className="overflow-auto flex-1">
          <table className="min-w-full text-sm text-gray-800">
            <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase tracking-wide">
              <tr>
                <th className="py-2.5 px-5 text-left">Type</th>
                <th className="py-2.5 px-5 text-left">Code</th>
                <th className="py-2.5 px-5 text-right">Discount %</th>
                <th className="py-2.5 px-5 text-right">GST %</th>
                <th className="py-2.5 px-5 text-left">HSN</th>
                <th className="py-2.5 px-5 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="py-10 text-center text-blue-400">
                  <i className="fas fa-spinner fa-spin text-2xl" />
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="py-10 text-center text-gray-400">
                  <i className="fas fa-tags text-4xl mb-3 block" />
                  <p className="text-sm">{searchTerm ? "No categories match your search." : "No categories found."}</p>
                </td></tr>
              ) : (
                filtered.map((cat, idx) => (
                  <tr key={cat.code}
                    className={`border-t transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-blue-50"} hover:bg-indigo-50`}>
                    <td className="py-2 px-5 font-medium">{getTypeName(cat)}</td>
                    <td className="py-2 px-5 font-mono text-xs text-gray-500">{cat.code}</td>
                    <td className="py-2 px-5 text-right text-gray-600">
                      {cat.discount ?? cat.defaultDiscount ?? "—"}
                    </td>
                    <td className="py-2 px-5 text-right text-gray-600">
                      {cat.gstRate != null ? `${cat.gstRate}%` : "—"}
                    </td>
                    <td className="py-2 px-5 font-mono text-xs text-gray-500">{cat.hsnCode || "—"}</td>
                    <td className="py-2 px-5 text-center">
                      <button onClick={() => handleEdit(cat)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-semibold transition">
                        <i className="fas fa-edit mr-1" />Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default ProductCategory;
